import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listReports, deleteReport } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, ArrowUpRight, FileText } from "lucide-react";

const stageColor = (stage) => {
  switch ((stage || "").toLowerCase()) {
    case "won": return "text-[#32D74B] border-[#32D74B]/40 bg-[#32D74B]/10";
    case "lost": return "text-[#FF453A] border-[#FF453A]/40 bg-[#FF453A]/10";
    case "proposal":
    case "meeting done": return "text-[#FF9F0A] border-[#FF9F0A]/40 bg-[#FF9F0A]/10";
    case "pitched":
    case "researched": return "text-[#0A84FF] border-[#0A84FF]/40 bg-[#0A84FF]/10";
    default: return "text-[#A3A3A3] border-[#262626] bg-[#1F1F1F]";
  }
};

export const SavedAccounts = ({ refreshKey }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const data = await listReports();
      setReports(data);
    } catch (e) {
      toast.error("Failed to load saved accounts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [refreshKey]);

  const onDelete = async (id, name, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete report for ${name}?`)) return;
    try {
      await deleteReport(id);
      toast.success("Report deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-sm text-[#737373] kpi-label">
        <span className="w-1.5 h-1.5 rounded-full bg-[#0A84FF] animate-pulse" />
        Loading saved accounts…
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="border border-dashed border-[#262626] rounded-sm p-10 text-center" data-testid="empty-saved-accounts">
        <FileText className="w-6 h-6 mx-auto text-[#525252] mb-3" strokeWidth={1.5} />
        <div className="kpi-label mb-1">/ NO ACCOUNTS YET</div>
        <p className="text-sm text-[#A3A3A3]">Compile your first target above.</p>
      </div>
    );
  }

  return (
    <div className="border border-[#262626] rounded-sm overflow-hidden" data-testid="saved-accounts-list">
      <div className="grid grid-cols-12 gap-3 px-4 py-2.5 bg-[#1F1F1F] border-b border-[#262626] kpi-label">
        <div className="col-span-4">Account</div>
        <div className="col-span-2">Stage</div>
        <div className="col-span-2">Score</div>
        <div className="col-span-2">Deal Value</div>
        <div className="col-span-2 text-right">Actions</div>
      </div>
      {reports.map((r, idx) => {
        const score = r?.report?.attractiveness_score?.score;
        const stage = r?.report?.pipeline_tracker?.stage || "Identified";
        const dealValue = r?.report?.pipeline_tracker?.deal_value || "—";
        const isPending = r?.status === "pending";
        const isFailed = r?.status === "failed";
        return (
          <Link
            key={r.id}
            to={`/report/${r.id}`}
            data-testid={`saved-account-row-${idx}`}
            className="grid grid-cols-12 gap-3 px-4 py-3.5 items-center border-b border-[#262626] last:border-b-0 hover:bg-[#1F1F1F] transition-colors group"
          >
            <div className="col-span-4 min-w-0">
              <div className="text-white font-display font-medium tracking-tight truncate flex items-center gap-2">
                {r.company_name}
                <ArrowUpRight className="w-3.5 h-3.5 text-[#525252] group-hover:text-[#0A84FF] transition-colors" strokeWidth={1.8}/>
              </div>
              <div className="kpi-label mt-0.5 truncate">
                {r.industry || "—"} · {r.geography || "Global"}{r.created_by_name ? ` · By ${r.created_by_name}` : ""}
              </div>
            </div>
            <div className="col-span-2">
              {isPending ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border text-[10px] font-mono-data tracking-wider uppercase text-[#0A84FF] border-[#0A84FF]/40 bg-[#0A84FF]/10">
                  <span className="w-1 h-1 rounded-full bg-[#0A84FF] animate-pulse"/>Compiling
                </span>
              ) : isFailed ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono-data tracking-wider uppercase text-[#FF453A] border-[#FF453A]/40 bg-[#FF453A]/10">
                  Failed
                </span>
              ) : (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono-data tracking-wider uppercase ${stageColor(stage)}`}>
                  {stage}
                </span>
              )}
            </div>
            <div className="col-span-2 font-mono-data text-sm">
              {score != null ? <span className="text-white">{score}<span className="text-[#525252]">/10</span></span> : <span className="text-[#525252]">—</span>}
            </div>
            <div className="col-span-2 font-mono-data text-sm text-[#A3A3A3] truncate">{isPending ? "—" : dealValue}</div>
            <div className="col-span-2 flex justify-end">
              <button
                type="button"
                onClick={(e) => onDelete(r.id, r.company_name, e)}
                data-testid={`delete-report-${idx}`}
                className="p-1.5 rounded-sm hover:bg-[#FF453A]/10 text-[#737373] hover:text-[#FF453A] transition-colors"
                aria-label="Delete report"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6}/>
              </button>
            </div>
          </Link>
        );
      })}
    </div>
  );
};

export default SavedAccounts;
